import { InfoCircledIcon } from "@radix-ui/react-icons";
import * as VisuallyHidden from "@radix-ui/react-visually-hidden";
import { Tooltip } from "antd";
import { ReactNode } from "react";

import { InfoIconButton } from "./AdminPage.styles";

interface IPageInfoTooltipProps {
  /**
   * What the page lists, and what it deliberately does not — the standing
   * explainer, not a message about the current result set. A note that changes
   * with the data ("no rows in this environment") belongs in the page body.
   */
  children: ReactNode;
  /**
   * The page's own title, so the control announces itself as "About Alerts"
   * rather than as one of several identical "more information" buttons.
   */
  title: string;
}

/**
 * The explainer for a page, behind an info icon beside its title (EN-5146 UI
 * feedback).
 *
 * These notes used to sit under the title as a bordered block. Every page
 * carried one, they ran to four or five lines on the wordier pages, and an
 * operator who had read it once paid for it on every visit by starting the
 * table that much further down the screen. The text is worth keeping — several
 * of them say what a page deliberately does NOT show, which is not guessable —
 * so it moves rather than goes.
 *
 * The copy is ALSO rendered visually hidden, immediately after the trigger. A
 * tooltip alone would put the explainer behind a hover, which is no answer for
 * a screen reader and no answer on a touch screen. It sits outside the
 * `Tooltip` rather than being wired up with `aria-describedby` because antd
 * clones its child and overwrites that attribute with the overlay's own id —
 * document order does the same job here and cannot be clobbered.
 */
export function PageInfoTooltip({ children, title }: IPageInfoTooltipProps) {
  return (
    <>
      <Tooltip
        title={children}
        placement="rightTop"
        // The longest of these runs about sixty words; antd's 250px default
        // would turn that into a very tall, very narrow column.
        styles={{ root: { maxWidth: "460px" } }}
      >
        <InfoIconButton type="button" aria-label={`About ${title}`}>
          <InfoCircledIcon aria-hidden />
        </InfoIconButton>
      </Tooltip>
      <VisuallyHidden.Root>{children}</VisuallyHidden.Root>
    </>
  );
}
